import React, { useEffect } from 'react';
import { Form, Input, Select, InputNumber, Row, Col, Divider } from 'antd';
import { FormConfig, FormFieldConfig } from '../DynamicForm';

// Fixed form fields configuration
const fixedFormFields: FormFieldConfig[] = [
  {
    key: "deviceCode",
    type: "input",
    label: "设备ID",
    required: true,
    placeholder: "请输入设备ID",
    rules: [{ required: true, message: "请输入设备ID" }],
  },
  {
    key: "deviceName",
    type: "input",
    label: "设备名称",
    required: true,
    placeholder: "请输入设备名称",
    rules: [{ required: true, message: "请输入设备名称" }],
  },
  {
    key: "deviceType",
    type: "select",
    label: "设备类型",
    required: true,
    placeholder: "请选择设备类型",
    options: [
      { label: "塔吊", value: 0 },
      { label: "扬尘", value: 1 },
      { label: "视频", value: 3 },
      { label: "卸料平台", value: 4 },
      { label: "施工升降机", value: 5 },
      { label: "围栏", value: 6 },
      { label: "高支模", value: 7 },
      { label: "考勤机", value: 9 },
      
    ],
    rules: [{ required: true, message: "请选择设备类型" }],
  },
  {
    key: "area",
    type: "number",
    label: "区域",
    required: false,
    placeholder: "请输入区域编号",
    props: { min: 0, disabled: true },
  },
];

// Helper function to render form items based on their type
const renderFormItem = (field: FormFieldConfig) => {
  const { type, placeholder, options, props = {} } = field;

  switch (type) {
    case "input":
      return <Input placeholder={placeholder} {...props} />;
    case "textarea":
      return <Input.TextArea placeholder={placeholder} rows={3} {...props} />;
    case "number":
      return (
        <InputNumber
          placeholder={placeholder}
          style={{ width: "100%" }}
          {...props}
        />
      );
    case "select":
      return (
        <Select placeholder={placeholder} {...props}>
          {options?.map((option) => (
            <Select.Option key={option.value} value={option.value}>
              {option.label}
            </Select.Option>
          ))}
        </Select>
      );
    default:
      return <Input placeholder={placeholder} {...props} />;
  }
};

interface FixedDeviceFormProps {
  form: any;
  initialValues: Record<string, any>;
  formConfig: FormConfig | null;
  onDeviceTypeChange: (value: number) => void;
}

const FixedDeviceForm: React.FC<FixedDeviceFormProps> = ({
  form,
  initialValues,
  formConfig,
  onDeviceTypeChange,
}) => {
  // Set form values when initialValues change
  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, initialValues]);

  return (
    <div className="form-section">
      <div className="form-section-header">
        <Divider orientation="center">基本信息</Divider>
      </div>
      <Row gutter={[16, 0]}>
        {fixedFormFields.map((field) => (
          <Col key={field.key} xs={24} sm={12} md={12}>
            <Form.Item
              name={field.key}
              label={field.label}
              rules={field.rules}
              required={field.required}
            >
              {field.type === "input" ? (
                <Input placeholder={field.placeholder} maxLength={128} />
              ) : field.type === "select" && field.key === "deviceType" ? (
                <Select 
                  placeholder={field.placeholder}
                  onChange={(value) => onDeviceTypeChange(value as number)}
                >
                  {field.options?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              ) : field.type === "select" ? (
                <Select placeholder={field.placeholder}>
                  {field.options?.map((option) => (
                    <Select.Option key={option.value} value={option.value}>
                      {option.label}
                    </Select.Option>
                  ))}
                </Select>
              ) : field.type === "number" ? (
                <InputNumber
                  placeholder={field.placeholder}
                  style={{ width: "100%" }}
                  {...field.props}
                />
              ) : null}
            </Form.Item>
          </Col>
        ))}
      </Row>

      {/* Extended Information Section - Only show if formConfig exists */}
      {formConfig && formConfig.fields.length > 0 && (
        <>
          <div className="form-section-header">
            <Divider orientation="center">扩展信息</Divider>
          </div>
          <Row gutter={[16, 0]}>
            {formConfig.fields
              .filter(
                (field) =>
                  !fixedFormFields.some(
                    (fixedField) =>
                      fixedField.key === field.key ||
                      (field.key === "device_code" &&
                        fixedField.key === "deviceCode") ||
                      (field.key === "device_name" &&
                        fixedField.key === "deviceName") ||
                      (field.key === "device_type" &&
                        fixedField.key === "deviceType") ||
                      (field.key === "area" && fixedField.key === "area")
                  )
              )
              .map((field) => (
                <Col key={field.key} xs={24} sm={12} md={12}>
                  <Form.Item
                    name={field.key}
                    label={field.label}
                    rules={field.rules}
                    required={field.required}
                  >
                    {renderFormItem(field)}
                  </Form.Item>
                </Col>
              ))}
          </Row>
        </>
      )}

      {/* Remarks Section */}
      <Form.Item name="remark" label="备注">
        <Input.TextArea placeholder="请输入备注" rows={3} maxLength={255} />
      </Form.Item>
    </div>
  );
};

export default FixedDeviceForm; 